import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Alert,
  FormHelperText,
  useTheme,
  alpha,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Account, accountsService } from './services/AccountsService';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { logger } from '../../utils/logger';
import apiService from '../../services/ApiService';
import Breadcrumb from '../../components/Breadcrumb';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import PlcSectionAccessPanel, { PlcSectionAccessPanelHandle } from './components/PlcSectionAccessPanel';
import { plcSectionsService, accountSupportsPlc } from './services/PlcSectionsService';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { environment } from '../../environments/environment';

const log = logger.getLogger('EditAccountPage');

interface Dashboard {
  id: number;
  name: string;
  resource: string;
  category?: {
    id: number;
    name: string;
    shortname: string;
    subtitle: string;
    orderBy: number;
    deactivated: boolean;
  };
  shortName?: string | null;
  superscript?: string;
  type: string;
  orderBy: number;
  listenReporting: boolean;
  hxcProgram: boolean;
  placeholder: boolean;
  icon: string;
  thumbnail: string;
  demo: string;
  downtime: string;
  internalId?: number;
  access?: boolean;
  hidden?: boolean;
}

interface DashboardAccess {
  access: boolean;
  hidden: boolean;
  id: number;
  name: string;
  type: string;
}

interface CompanyHierarchy {
  companyHierarchy: {
    sectorList: string[];
    sectors: {
      [sectorName: string]: {
        industryList: string[];
        sectorName: string;
        sectorId: number;
        industries: {
          [industryName: string]: {
            companyList: string[];
            industryName: string;
            industryId: number;
            companies: {
              [companyName: string]: {
                companyName: string;
                companyId: number;
              };
            };
          };
        };
      };
    };
  };
  trailingPeriods: string[];
  ageGroupValues: string[];
  genderValues: string[];
  ethnicityValues: string[];
  startDate: string;
  endDate: string;
}

interface AccessRestrictor {
  id: number;
  accountId: number;
  bigQueryId: number;
  sector: string;
  industry: string;
  type: "INDUSTRY" | "COMPANY";
  name: string;
  active: boolean;
  createdBy: number;
  createDate: string;
  updatedBy: number;
  updateDate: string;
}

interface DashboardAction {
  add: number[];
  remove: number[];
  show: number[];
  hide: number[];
}

interface FormState {
  name: string;
  lineOfBusiness: string;
  supersetRole: string;
  ssoEnabled: boolean;
  persistent: boolean;
  listenPortalEnabled: boolean;
  listenReportingOnly: boolean; // Optional field for listen reporting
  strategyPortalEnabled: boolean;
  selectiveAccess: boolean;
  sfdcAccountId: string; // SFDC Account Id field
  embeddedDashboards: Dashboard[];
  hiddenDashboards: Dashboard[];
  selectedIndustries: { id: number; name: string; sector: string }[];
  selectedCompanies: { [industryId: string]: { id: number; name: string; sector: string; industry: string }[] };
  restrictionAccess: any[];
}

const lineOfBusinessOptions = ["B2B", "EFG", "AFF", "AGG", "STA"];

// Convert an account's restrictionAccess entries into the industry/company selections the form uses.
// INDUSTRY entries always resolve; COMPANY entries need the company hierarchy to map back to their
// parent industry, so callers should re-run this once companyHierarchy has loaded.
const buildRestrictionSelections = (
  restrictionAccess: any[] | undefined,
  companyHierarchy: CompanyHierarchy | null
): {
  industries: { id: number; name: string; sector: string }[];
  companies: { [industryId: string]: { id: number; name: string; sector: string; industry: string }[] };
} => {
  const industries: { id: number; name: string; sector: string }[] = [];
  const companies: { [industryId: string]: { id: number; name: string; sector: string; industry: string }[] } = {};

  if (!Array.isArray(restrictionAccess)) {
    return { industries, companies };
  }

  // Resolve a company restriction's parent industry id (the key its companies are grouped under)
  // via a direct O(1) name lookup — the hierarchy is keyed by sector and industry names, both of
  // which the restriction carries. Returns 0 if the industry isn't present in the hierarchy.
  const findIndustryId = (restriction: any): number => {
    const industry = companyHierarchy?.companyHierarchy.sectors[restriction.sector]
      ?.industries[restriction.industry];
    return industry ? industry.industryId : 0;
  };

  // Single pass over the restrictions, branching on type per entry. Each selection stores its full
  // sector/industry chain so later lookups and the submit payload never have to reverse-map.
  restrictionAccess.forEach(restriction => {
    if (restriction.type === 'INDUSTRY') {
      industries.push({
        id: restriction.bigQueryId,
        name: restriction.name || restriction.industry, // Use name if available, otherwise industry
        sector: restriction.sector
      });
    } else if (restriction.type === 'COMPANY') {
      const industryId = findIndustryId(restriction);
      if (industryId) {
        if (!companies[industryId]) {
          companies[industryId] = [];
        }
        companies[industryId].push({
          id: restriction.bigQueryId,
          name: restriction.name,
          sector: restriction.sector,
          industry: restriction.industry
        });
      }
    }
  });

  return { industries, companies };
};

// Custom MultiSelect component for handling large datasets
const MultiSelect = ({
  id,
  label,
  options,
  value,
  onChange,
  placeholder = 'Select items',
  groupBy,
  renderGroup,
  helperText,
  filterSelectedOptions = false,
  disabled = false,
}: {
  id: string;
  label: string;
  options: any[];
  value: any[];
  onChange: (event: React.SyntheticEvent, value: any[]) => void;
  placeholder?: string;
  groupBy?: (option: any) => string;
  renderGroup?: (params: any) => React.ReactNode;
  helperText?: string;
  filterSelectedOptions?: boolean;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Initialize all groups as expanded by default
  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({});

  // Helper function to get ID from an option
  const getOptionId = (option: any) => {
    if (typeof option === 'string') return option;
    return option.id !== undefined ? option.id : option.name;
  };

  // Helper function to get name from an option
  const getOptionName = (option: any) => {
    return typeof option === 'string' ? option : option.name;
  };

  // Get real options (excluding Select All option)
  const realOptions = React.useMemo(() => options.filter(option => 
    !(typeof option === 'object' && (option.name === 'Select All' || option.id === -1))
  ), [options]);
  
  // Get real selected values (excluding Select All option)
  const realSelectedValues = React.useMemo(() => value.filter(v => 
    typeof v === 'object' ? v.name !== 'Select All' && v.id !== -1 : true
  ), [value]);
  
  // Check if all options are selected - compare real options count with real selected values count
  const allSelected = React.useMemo(() => {
    // No real options means nothing is selected
    if (realOptions.length === 0) return false;
    
    // For simple string options
    if (typeof realOptions[0] === 'string') {
      const optionSet = new Set(realOptions);
      return realSelectedValues.length === optionSet.size && 
             realSelectedValues.every(val => 
               optionSet.has(typeof val === 'string' ? val : val.name)
             );
    }
    
    // For object options, compare by ID
    const selectedIds = new Set(realSelectedValues.map(v => getOptionId(v)));
    const allOptionIds = realOptions.map(o => getOptionId(o));
    
    return realSelectedValues.length === realOptions.length &&
           allOptionIds.every(id => selectedIds.has(id));
  }, [realOptions, realSelectedValues]);
  
  // Count selected items but filter out the 'Select All' option
  const selectedCount = realSelectedValues.length;

  // Filter out any "Select All" entries from actually displayed value
  const displayValue = realSelectedValues;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize expandedGroups when options or groupBy change
  useEffect(() => {
    if (groupBy) {
      const groups: { [key: string]: boolean } = {};
      options.forEach(option => {
        const group = groupBy(option) || 'Other';
        if (groups[group] === undefined) {
          // Set all groups to expanded by default
          groups[group] = true;
        }
      });
      setExpandedGroups(groups);
    }
  }, [options, groupBy]);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
    }
  };

  const handleToggle = (option: any) => {
    const currentValue = [...value];
    const optionId = getOptionId(option);
    
    // Check if option is already selected by comparing IDs
    const isSelected = currentValue.some(item => getOptionId(item) === optionId);

    let newValue;
    if (isSelected) {
      // Remove the item if it's already selected (by ID)
      newValue = currentValue.filter(item => getOptionId(item) !== optionId);
    } else {
      // Add the item if it's not selected
      newValue = [...currentValue, option];
    }

    onChange({} as React.SyntheticEvent, newValue);
  };

  const handleClearAll = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Deselect all items
    onChange({} as React.SyntheticEvent, []);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Select All clicked, checked state:', event.target.checked, 'real options:', realOptions.length);
    
    if (event.target.checked) {
      // Select all real options (excluding any "Select All" option)
      onChange({} as React.SyntheticEvent, [...realOptions]);
    } else {
      // Deselect all options
      onChange({} as React.SyntheticEvent, []);
    }
  };

  // Handle category expansion toggle
  const toggleCategory = (category: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Filter options based on search query
  const filteredOptions = searchQuery 
    ? options.filter(option => {
        const optionLabel = typeof option === 'string' ? option : option.name;
        return optionLabel.toLowerCase().includes(searchQuery.toLowerCase());
      })
    : options;

  // Group options if needed
  const groupedOptions: { [key: string]: any[] } = {};
  
  // Track which groups match by name for auto-expansion
  const groupsMatchingSearch = React.useMemo<string[]>(() => [], []);
  
  if (groupBy) {
    // First, collect all options by their groups
    const allGroupedOptions: { [key: string]: any[] } = {};
    options.forEach(option => {
      const group = groupBy(option) || 'Other';
      if (!allGroupedOptions[group]) allGroupedOptions[group] = [];
      allGroupedOptions[group].push(option);
    });
    
    // Then, if there's a search query, only include groups that match the search
    // or groups that have at least one matching option
    if (searchQuery) {
      Object.keys(allGroupedOptions).forEach(group => {
        // Check if the group name matches the search
        const groupNameMatches = group.toLowerCase().includes(searchQuery.toLowerCase());
        
        // Check if any option in the group matches the search
        const matchingOptions = allGroupedOptions[group].filter(option => {
          const optionLabel = typeof option === 'string' ? option : option.name;
          return optionLabel.toLowerCase().includes(searchQuery.toLowerCase());
        });
        
        // Add the group if either the group name matches or it has matching options
        if (groupNameMatches || matchingOptions.length > 0) {
          // If the group name matches, include all options from that group
          groupedOptions[group] = groupNameMatches 
            ? allGroupedOptions[group]
            : matchingOptions;
          
          // Track groups that match by name (for auto-expansion later)
          if (groupNameMatches) {
            groupsMatchingSearch.push(group);
          }
        }
      });
    } else {
      // If no search query, use all groups
      Object.keys(allGroupedOptions).forEach(group => {
        groupedOptions[group] = allGroupedOptions[group];
      });
    }
  } else {
    // If no grouping, just filter options directly
    filteredOptions.forEach(option => {
      const group = 'All';
      if (!groupedOptions[group]) groupedOptions[group] = [];
      groupedOptions[group].push(option);
    });
  }
  
  // Auto-expand matching groups in a useEffect to avoid render-time state updates
  useEffect(() => {
    if (searchQuery && groupsMatchingSearch.length > 0) {
      const updatedGroups = {...expandedGroups};
      let hasChanges = false;
      
      groupsMatchingSearch.forEach(group => {
        if (!updatedGroups[group]) {
          updatedGroups[group] = true;
          hasChanges = true;
        }
      });
      
      if (hasChanges) {
        setExpandedGroups(updatedGroups);
      }
    }
  }, [searchQuery, expandedGroups, groupsMatchingSearch]);

  return (
    <FormControl fullWidth>
      <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
        <Button
          id={id}
          onClick={toggleDropdown}
          aria-controls={isOpen ? `${id}-menu` : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          variant="outlined"
          fullWidth
          disabled={disabled}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            justifyContent: 'space-between',
            textAlign: 'left',
            py: 1.3,
            borderColor: theme.palette.divider,
            textTransform: 'none',
            '&:hover': {
              borderColor: theme.palette.text.primary,
            },
            ...(isOpen && {
              borderColor: theme.palette.primary.main,
            }),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
              color={selectedCount > 0 ? 'text.primary' : 'text.secondary'}
              sx={{ 
                flexGrow: 1,
                maxWidth: '100%',
                textTransform: 'none',
                fontWeight: 'normal',
                textOverflow: 'initial',
                whiteSpace: 'normal'
              }}
            >
              {selectedCount > 0 
                ? `${selectedCount} item${selectedCount !== 1 ? 's' : ''} selected` 
                : placeholder}
            </Typography>
          </Box>
        </Button>
        
        {/* Custom dropdown menu */}
        {isOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              minWidth: 400,
              zIndex: 1300,
              mt: 0.5,
              bgcolor: 'background.paper',
              boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
              borderRadius: 1,
              maxHeight: 400,
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box p={2}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 1.5
              }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {label}
                </Typography>
                
                <div
                  onClick={(e) => { 
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectedCount > 0) handleClearAll();
                  }}
                  style={{ 
                    cursor: selectedCount > 0 ? 'pointer' : 'default',
                    color: selectedCount > 0 ? theme.palette.primary.main : theme.palette.text.disabled,
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {selectedCount > 0 && 'Clear All'}
                </div>
              </Box>
              
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel 
                  control={
                    <Checkbox 
                      checked={allSelected && realOptions.length > 0}
                      indeterminate={selectedCount > 0 && !allSelected && realOptions.length > 0}
                      onChange={handleSelectAll}
                      disabled={realOptions.length === 0}
                    />
                  } 
                  label="Select All" 
                />
              </Box>
              
              {/* Search box */}
              <TextField
                fullWidth
                placeholder="Search options..."
                size="small"
                value={searchQuery}
                onChange={(e: any) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              {/* Selected items display inside dropdown */}
              {displayValue.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Selected items:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {displayValue.map((item, index) => {
                      const label = typeof item === 'string' 
                        ? item 
                        : item.displayName || (item?.superscript?.trim() ? `${item.name} - ${item.superscript}` : item.name);
                      return (
                        <Chip 
                          key={index}
                          label={label}
                          size="small"
                          onDelete={() => handleToggle(item)}
                          sx={{ 
                            borderRadius: '16px',
                            '.MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '220px'
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              
              <Divider sx={{ mb: 1 }} />
              
              {/* Option list with groups */}
              {groupBy && renderGroup ? (
                // Render grouped options with expandable categories
                Object.keys(groupedOptions).map((group) => {
                  // Skip empty group used for Select All
                  if (group === '' && groupedOptions[group].every(o => 
                    typeof o === 'object' && (o.name === 'Select All' || o.id === -1)
                  )) {
                    return null;
                  }
                  
                  // Skip "Other" category if it's empty or only contains the Select All option
                  if ((group === 'Other' || group === 'Uncategorized') && 
                      (groupedOptions[group].length === 0 || 
                       groupedOptions[group].every(o => 
                         typeof o === 'object' && (o.name === 'Select All' || o.id === -1)
                       ))
                  ) {
                    return null;
                  }

                  const isExpanded = expandedGroups[group] !== false; // Default to expanded
                  
                  return (
                    <Box key={group} sx={{ mb: 1 }}>
                      {/* Category header */}
                      <Box
                        onClick={() => toggleCategory(group)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1,
                          backgroundColor: theme.palette.mode === 'dark'
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                          borderRadius: 1,
                          cursor: 'pointer',
                          mb: 0.5,
                        }}
                      >
                        <KeyboardArrowDownIcon
                          sx={{
                            mr: 1,
                            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          {group.split('___')[0]} {/* Display only the category name, not the unique ID */}
                        </Typography>
                      </Box>
                      
                      {/* Items in category */}
                      <Box
                        sx={{
                          display: isExpanded ? 'block' : 'none',
                          ml: 1,
                        }}
                      >
                        {groupedOptions[group]
                          .filter(option => !(typeof option === 'object' && (option.name === 'Select All' || option.id === -1)))
                          .map(option => {
                            const optionName = getOptionName(option);
                            const optionId = getOptionId(option);
                            const isSelected = value.some(item => getOptionId(item) === optionId);

                            return (
                              <Box
                                key={typeof option === 'string' ? option : option.id || option.name}
                                onClick={() => handleToggle(option)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  p: 0.5,
                                  pl: 1,
                                  borderRadius: 1,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  },
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  disableRipple
                                />
                                <Typography variant="body2" sx={{ ml: 0.5 }}>
                                  {optionName}
                                </Typography>
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  );
                })
              ) : (
                // Render flat options without grouping
                <List dense sx={{ 
                  width: '100%', 
                  maxHeight: 300, 
                  overflow: 'auto',
                  '& .MuiListItem-root': {
                    px: 1
                  }
                }}>
                  {filteredOptions
                    .filter(option => !(typeof option === 'object' && (option.name === 'Select All' || option.id === -1)))
                    .map(option => {
                      const optionName = getOptionName(option);
                      const optionId = getOptionId(option);
                      const isSelected = value.some(item => getOptionId(item) === optionId);
                      
                      return (
                        <ListItemButton 
                          key={typeof option === 'string' ? option : option.id || option.name}
                          dense
                          onClick={() => handleToggle(option)}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={optionName} 
                            primaryTypographyProps={{
                              noWrap: true,
                              fontSize: '0.875rem'
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                </List>
              )}
              
              {filteredOptions.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">No options found</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </div>
      
      {/* Selected items display */}
      {displayValue.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {displayValue.slice(0, 5).map((item, index) => {
            // Use displayName if available (for dashboard objects with superscript), otherwise fall back to name
            const label = typeof item === 'string' 
              ? item 
              : item.displayName || (item?.superscript?.trim() ? `${item.name} - ${item.superscript}` : item.name);
            
            return (
              <Chip 
                key={index}
                label={label}
                size="small"
                onDelete={() => handleToggle(item)}
                sx={{ 
                  borderRadius: '16px',
                  '.MuiChip-label': {
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            );
          })}
          {displayValue.length > 5 && (
            <Chip
              label={`+${displayValue.length - 5} more`}
              size="small"
              sx={{ borderRadius: '16px' }}
              onClick={toggleDropdown} // Open the dropdown when clicking on "+X more" chip
            />
          )}
          <div
            onClick={(e) => { 
              e.preventDefault();
              e.stopPropagation();
              handleClearAll();
            }}
            style={{ 
              cursor: 'pointer',
              color: theme.palette.primary.main,
              fontWeight: 500,
              fontSize: '0.75rem',
              padding: '4px',
              marginLeft: '4px'
            }}
          >
            Clear All
          </div>
        </Box>
      )}
      
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

const EditAccountPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // Get return URL from navigation state, fallback to /accounts
  const returnTo = (location.state as { returnTo?: string })?.returnTo || '/accounts';
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Active/inactive state
  const [formActive, setFormActive] = useState<boolean>(true);
  const [originalActive, setOriginalActive] = useState<boolean>(true);

  // SSO Modal state
  const [originalSsoEnabled, setOriginalSsoEnabled] = useState<boolean>(false);
  const [showSsoModal, setShowSsoModal] = useState<boolean>(false);
  const [pendingSsoValue, setPendingSsoValue] = useState<boolean>(false);
  const [pendingSubmit, setPendingSubmit] = useState<boolean>(false);

  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [companyHierarchy, setCompanyHierarchy] = useState<CompanyHierarchy | null>(null);
  // True when the company hierarchy (from data-svc) failed to load. Used to hide the selective-access
  // section and keep its data out of the create payload, without blanking the whole form.
  const [companyHierarchyUnavailable, setCompanyHierarchyUnavailable] = useState(false);
  const plcPanelRef = useRef<PlcSectionAccessPanelHandle>(null);

  const [formState, setFormState] = useState<FormState>({
    name: '',
    lineOfBusiness: 'B2B',
    supersetRole: '',
    ssoEnabled: false,
    persistent: false,
    listenPortalEnabled: false,
    strategyPortalEnabled: false,
    listenReportingOnly: false,
    selectiveAccess: false,
    sfdcAccountId: '',
    embeddedDashboards: [],
    hiddenDashboards: [],
    selectedIndustries: [],
    selectedCompanies: {},
    restrictionAccess: []
  });

  const isEditMode = id && id !== 'create';
  const pageTitle = isEditMode ? `Edit Account: ${account?.name}` : 'Create Account';
  // isDeactivated tracks the form's current active checkbox — unlocks fields when re-activating
  const isDeactivated = isEditMode && formActive === false;
  const activeChanged = isEditMode && formActive !== originalActive;

  useEffect(() => {
    if (isEditMode) {
      const fetchAccount = async () => {
        try {
          setLoading(true);
          setError(null); // Clear any existing error messages
          // User is authenticated, proceed with fetching account data
          const accountData = await accountsService.getAccount(Number(id));
          setAccount(accountData);

          // Process restriction access into industry/company selections. companyHierarchy is often
          // still loading on this first pass (so companies may not resolve yet); the effect below
          // reprocesses once the hierarchy is available.
          const { industries, companies } = buildRestrictionSelections(
            accountData.restrictionAccess,
            companyHierarchy
          );

          // Initialize form state with account data, but preserve existing dashboard selections
          setFormState(prev => ({
            ...prev,
            name: accountData.name || '',
            lineOfBusiness: accountData.lineOfBusiness || 'B2B',
            supersetRole: accountData.supersetRole || '',
            ssoEnabled: accountData.ssoEnabled || false,
            persistent: accountData.persistent || false,
            listenPortalEnabled: accountData.listenPortalEnabled || false,
            strategyPortalEnabled: accountData.strategyPortalEnabled || false,
            listenReportingOnly: accountData.listenReportingOnly || false,
            selectiveAccess: accountData.selectiveAccess || false,
            sfdcAccountId: accountData.sfdcAccountId || '',
            selectedIndustries: industries,
            selectedCompanies: companies,
            restrictionAccess: accountData.restrictionAccess || []
          }));

          // Store the original SSO value for comparison
          setOriginalSsoEnabled(accountData.ssoEnabled || false);

          // Store active state (for status endpoint)
          const loadedActive = accountData.active !== undefined ? accountData.active : true;
          setFormActive(loadedActive);
          setOriginalActive(loadedActive);

          setLoading(false);
        } catch (err) {
          log.error('Error fetching account:', err);
          setError('Failed to load account details. Please try again later.');
          setLoading(false);
        }
      };

      fetchAccount();
    } else {
      setLoading(false);
    }
    // Note: companyHierarchy is used inside fetchAccount for sector ID mapping,
    // but we intentionally exclude it from dependencies to avoid double-fetching.
    // The UI's groupBy uses companyHierarchy directly, so display still works correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  // Reprocess restriction access once the company hierarchy is available. On the initial account
  // fetch the hierarchy is usually still loading, which leaves company selections empty (industries
  // resolve without it, but companies need it to map back to their parent industry).
  useEffect(() => {
    if (!account || !companyHierarchy) {
      return;
    }
    const { industries, companies } = buildRestrictionSelections(
      account.restrictionAccess,
      companyHierarchy
    );
    setFormState(prev => ({
      ...prev,
      selectedIndustries: industries,
      selectedCompanies: companies
    }));
  }, [account, companyHierarchy]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const dashboardsResponse = await apiService.makeRequest(
          'get',
          `${environment.services.account.baseUrl}${environment.services.account.basePath}/portal/dashboard/options`
        );

        if (dashboardsResponse && dashboardsResponse.dashboards) {
          setDashboards(dashboardsResponse.dashboards);
        }

        const accessResponse = await apiService.makeRequest(
          'get',
          `${environment.services.account.baseUrl}${environment.services.account.basePath}/portal/dashboard/access/${id}/showViz`
        );

        if (accessResponse && accessResponse.dashboards) {
          // Filter dashboards that have access flag set to true
          let embedded = accessResponse.dashboards
            .filter((d: DashboardAccess) => d.access)
            // Add displayName to each dashboard based on superscript availability
            .map((dashboard: any) => {
              // Find the full dashboard info from dashboardsResponse to get superscript
              const fullDashboard = dashboardsResponse.dashboards.find(
                (d: Dashboard) => d.id === dashboard.id
              );
              
              return {
                ...dashboard,
                superscript: fullDashboard?.superscript?.trim(),
                displayName: fullDashboard?.superscript?.trim() 
                  ? `${dashboard.name} - ${fullDashboard.superscript}` 
                  : dashboard.name
              };
            });
            
          // Filter dashboards that have hidden flag set to true
          let hidden = accessResponse.dashboards
            .filter((d: DashboardAccess) => d.hidden)
            // Add displayName to each dashboard based on superscript availability
            .map((dashboard: any) => {
              // Find the full dashboard info from dashboardsResponse to get superscript
              const fullDashboard = dashboardsResponse.dashboards.find(
                (d: Dashboard) => d.id === dashboard.id
              );
              
              return {
                ...dashboard,
                superscript: fullDashboard?.superscript?.trim(),
                displayName: fullDashboard?.superscript?.trim() 
                  ? `${dashboard.name} - ${fullDashboard.superscript}` 
                  : dashboard.name
              };
            });

          setFormState(prev => ({
            ...prev,
            embeddedDashboards: embedded,
            hiddenDashboards: hidden
          }));
        }
      } catch (err) {
        log.error('Err fetching dashboard data:', err);
        setError('Failed to load dashboard data. Some features may be limited.');
      }
    };

    const fetchDashboardDataCreate = async () => {
      try {
        const dashboardsResponse = await apiService.makeRequest(
          'get',
          `${environment.services.account.baseUrl}${environment.services.account.basePath}/portal/dashboard/options`
        );

        if (dashboardsResponse && dashboardsResponse.dashboards) {
          setDashboards(dashboardsResponse.dashboards);
        }
      } catch (err) {
        log.error('Error fetching company hierarchy:', err);
        setError('Failed to load industry data. Some features may be limited.');
      }
    };

    const fetchCompanyHierarchy = async () => {
      try {
        const hierarchyResponse = await apiService.makeRequest(
          'get',
          `${environment.services.data.baseUrl}${environment.services.data.basePath}/timeseries/monthly/options`
        );

        if (hierarchyResponse) {
          setCompanyHierarchy(hierarchyResponse);
          setCompanyHierarchyUnavailable(false);
        }
      } catch (err) {
        // The company hierarchy comes from data-svc, which may be unreachable (e.g. local dev with
        // no data-svc access). Don't surface a global error here — in create mode that would blank
        // the entire form via the `error && !account` guard below. Instead flag it as unavailable so
        // the selective-access section simply hides and its data is excluded from submission.
        log.error('Error fetching company hierarchy:', err);
        setCompanyHierarchyUnavailable(true);
      }
    };

    if (isEditMode) {
      fetchDashboardData();
    } else {
      fetchDashboardDataCreate();
    }
    // fetchDashboardData();
    fetchCompanyHierarchy();
  }, [id, isEditMode]);

  const handleInputChange = (field: string, value: any) => {
    if (field === 'selectiveAccess' && value === false) {
      // If selective access is being disabled, reset the related fields
      setFormState(prev => ({
        ...prev,
        [field]: value,
        selectedIndustries: [],
        selectedCompanies: {},
        restrictionAccess: [] // Explicitly clear restrictionAccess array
      }));
    } else {
      setFormState(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle SSO toggle change with modal warning
  const handleSsoToggle = (checked: boolean) => {
    // If in edit mode and the value is different from original, show modal
    if (isEditMode && checked !== originalSsoEnabled) {
      setPendingSsoValue(checked);
      setShowSsoModal(true);
    } else {
      // If toggling back to original value or in create mode, just update
      setFormState(prev => ({
        ...prev,
        ssoEnabled: checked
      }));
    }
  };

  // Handle SSO modal confirmation
  const handleSsoModalConfirm = () => {
    setFormState(prev => ({
      ...prev,
      ssoEnabled: pendingSsoValue
    }));
    setShowSsoModal(false);
    
    // If there's a pending submit, trigger it after updating the state
    if (pendingSubmit) {
      setPendingSubmit(false);
      // Use setTimeout to ensure state update completes first
      setTimeout(() => {
        performSubmit();
      }, 0);
    }
  };

  // Handle SSO modal cancellation
  const handleSsoModalCancel = () => {
    // Reset to current value (which should be the value before the toggle)
    setShowSsoModal(false);
    setPendingSubmit(false);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if SSO has changed and show modal if not already confirmed
    if (isEditMode && formState.ssoEnabled !== originalSsoEnabled) {
      setPendingSsoValue(formState.ssoEnabled);
      setPendingSubmit(true);
      setShowSsoModal(true);
      return; // Prevent form submission until confirmed
    }
    
    // If SSO hasn't changed or we're in create mode, proceed with submission
    await performSubmit();
  };

  // Separate function to perform the actual submission
  const performSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Build the full restrictionAccess set from the current selections, using the property names
      // the account-svc API expects: bigQueryId, sector, industry, type, name. Each selection carries
      // its full sector/industry chain, so this is a direct read with no hierarchy lookups.
      // The selective-access section depends on the company hierarchy from data-svc. When creating an
      // account and that data failed to load, the section is hidden — so exclude its data from the
      // payload. Edit mode keeps its existing behavior (isEditMode short-circuits the hierarchy check).
      const selectiveAccessActive = formState.selectiveAccess && (isEditMode || companyHierarchy !== null);

      const restrictionAccess: any[] = [];

      if (selectiveAccessActive) {
        // First add all selected industries
        for (const industry of formState.selectedIndustries) {
          restrictionAccess.push({
            type: 'INDUSTRY',
            bigQueryId: industry.id,
            name: industry.name,
            sector: industry.sector,
            industry: industry.name
          });
        }

        // Then add all selected companies, grouped by industry
        for (const industryId of Object.keys(formState.selectedCompanies)) {
          const companies = formState.selectedCompanies[industryId] || [];
          for (const company of companies) {
            restrictionAccess.push({
              type: 'COMPANY',
              bigQueryId: company.id,
              name: company.name,
              sector: company.sector,
              industry: company.industry
            });
          }
        }
      }
      // For non-selective access, restrictionAccess remains an empty array

      const accountData = {
        name: formState.name,
        lineOfBusiness: formState.lineOfBusiness,
        supersetRole: formState.supersetRole.trim(),
        ssoEnabled: formState.ssoEnabled,
        persistent: formState.persistent,
        listenPortalEnabled: formState.listenPortalEnabled,
        listenReportingOnly: formState.listenReportingOnly,
        strategyPortalEnabled: formState.strategyPortalEnabled,
        selectiveAccess: selectiveAccessActive,
        sfdcAccountId: formState.sfdcAccountId,
        restrictionAccess: restrictionAccess // Full current set; empty when selective access is disabled
      };

      let savedAccount;
      if (isEditMode && account) {
        savedAccount = await accountsService.updateAccount({
          ...account,
          ...accountData,
          restrictionAccess: selectiveAccessActive ? restrictionAccess : []
        });
      } else {
        savedAccount = await accountsService.createAccount(accountData as Omit<Account, 'id'>);
      }

      // Calculate dashboard actions based on selected dashboards
      const allDashboardIds = dashboards.map(d => d.id);
      const embeddedIds = formState.embeddedDashboards.map(d => d.id);
      const hiddenIds = formState.hiddenDashboards.map(d => d.id);
      
      // Calculate the IDs for dashboards that aren't specifically included in either embedded or hidden
      const removeIds = allDashboardIds.filter(id => !embeddedIds.includes(id));
      const showIds = allDashboardIds.filter(id => !hiddenIds.includes(id));
      const dashboardActions: DashboardAction = {
        add: embeddedIds, // Dashboards to be added with access
        remove: removeIds, // Dashboards to be removed (those not explicitly selected)
        show: showIds, // Dashboards to be shown
        hide: hiddenIds // Dashboards to be hidden
      };

      // Use the actual account ID (or the newly created account's ID)
      const accountId = isEditMode ? id : savedAccount.id;
      
      await apiService.makeRequest(
        'put',
        `${environment.services.account.baseUrl}${environment.services.account.basePath}/portal/dashboard/access/${accountId}`,
        dashboardActions
      );

      // If active status changed, fire the status endpoint
      if (activeChanged && isEditMode) {
        await accountsService.changeAccountStatus(Number(accountId), formActive);
        setOriginalActive(formActive);
      }

      // Apply staged PLC section changes (saved per section against plc-svc). Handled separately
      // so a partial failure can name the sections that failed while keeping the rest saved.
      if (plcPanelRef.current) {
        try {
          await plcPanelRef.current.applySectionChanges();
        } catch (plcErr) {
          log.error('Error applying PLC section changes:', plcErr);
          setError(
            plcErr instanceof Error
              ? plcErr.message
              : 'Failed to save PLC section changes. Please try again.'
          );
          return;
        }
      }

      // Invalidate caches to ensure fresh data is loaded
      apiService.invalidateCacheByPattern('/accounts');
      apiService.invalidateCacheByPattern(`/portal/dashboard/access/${accountId}`);

      setSuccessMessage(`Account ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => {
        navigate(returnTo);
      }, 1500);

    } catch (err) {
      log.error(`Error ${isEditMode ? 'updating' : 'creating'} account:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} account. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(returnTo);
  };

  // helper functions near your other handler functions

  const handleSelectAllDashboards = (type: 'embedded' | 'hidden') => {
    if (type === 'embedded') {
      // Select all available dashboards for embedding
      const availableDashboards = dashboards.filter(dashboard =>
        !formState.hiddenDashboards.some(hidden => hidden.id === dashboard.id)
      );
      setFormState(prev => ({
        ...prev,
        embeddedDashboards: [...prev.embeddedDashboards, ...availableDashboards]
      }));
    } else {
      // Select all available dashboards for hiding
      const availableDashboards = dashboards.filter(dashboard =>
        !formState.embeddedDashboards.some(embedded => embedded.id === dashboard.id)
      );
      setFormState(prev => ({
        ...prev,
        hiddenDashboards: [...prev.hiddenDashboards, ...availableDashboards]
      }));
    }
  };

  const handleSelectAllIndustries = () => {
    if (!companyHierarchy) return;

    const allIndustries = companyHierarchy.companyHierarchy.sectorList.flatMap(sector =>
      companyHierarchy.companyHierarchy.sectors[sector].industryList.map(industry => {
        const industryData = companyHierarchy.companyHierarchy.sectors[sector].industries[industry];
        return {
          id: industryData.industryId,
          name: industryData.industryName,
          sector: companyHierarchy.companyHierarchy.sectors[sector].sectorName
        };
      })
    );

    setFormState(prev => ({
      ...prev,
      selectedIndustries: allIndustries
    }));
  };

  const handleSelectAllCompanies = (industry: { id: number; name: string; sector: string }) => {
    if (!companyHierarchy) return;

    const industryData = companyHierarchy.companyHierarchy.sectors[industry.sector]
      ?.industries[industry.name];
    if (!industryData) return;

    const companiesList = Object.values(industryData.companies).map(company => ({
      id: company.companyId,
      name: company.companyName,
      sector: industry.sector,
      industry: industry.name
    }));

    setFormState(prev => ({
      ...prev,
      selectedCompanies: {
        ...prev.selectedCompanies,
        [industry.id]: companiesList
      }
    }));
  };

  const handleEmbeddedDashboardChange = (event: React.SyntheticEvent, value: Dashboard[]) => {
    setFormState(prev => {
      // Remove any dashboards that are now in embedded from the hidden list
      const updatedHiddenDashboards = prev.hiddenDashboards.filter(
        dashboard => !value.some(embedded => embedded.id === dashboard.id)
      );

      // Preserve any superscript in the dashboard objects
      const formattedValue = value.map(dashboard => ({
        ...dashboard,
        displayName: dashboard?.superscript?.trim() ? `${dashboard.name} - ${dashboard.superscript}` : dashboard.name
      }));

      return {
        ...prev,
        embeddedDashboards: formattedValue,
        hiddenDashboards: updatedHiddenDashboards
      };
    });
  };

  const handleHiddenDashboardChange = (event: React.SyntheticEvent, value: Dashboard[]) => {
    setFormState(prev => {
      // Remove any dashboards that are now in hidden from the embedded list
      const updatedEmbeddedDashboards = prev.embeddedDashboards.filter(
        dashboard => !value.some(hidden => hidden.id === dashboard.id)
      );

      // Preserve any superscript in the dashboard objects
      const formattedValue = value.map(dashboard => ({
        ...dashboard,
        displayName: dashboard?.superscript?.trim() ? `${dashboard.name} - ${dashboard.superscript}` : dashboard.name
      }));
      
      return {
        ...prev,
        hiddenDashboards: formattedValue,
        embeddedDashboards: updatedEmbeddedDashboards
      };
    });
  };

  const handleIndustryChange = (event: React.SyntheticEvent, value: { id: number; name: string; sector: string }[]) => {
    setFormState(prev => {
      const updatedCompanies = { ...prev.selectedCompanies };
      Object.keys(updatedCompanies).forEach(industryId => {
        if (!value.some(industry => industry.id === Number(industryId))) {
          delete updatedCompanies[industryId];
        }
      });

      return {
        ...prev,
        selectedIndustries: value,
        selectedCompanies: updatedCompanies  
      };
    });
  };

  const handleCompanyChange = (industryId: number, companies: { id: number; name: string; sector: string; industry: string }[]) => {
    setFormState(prev => ({
      ...prev,
      selectedCompanies: {
        ...prev.selectedCompanies,
        [industryId]: companies
      }
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={60} thickness={4} color="primary" />
        </Box>
      </Container>
    );
  }

  if (error && !account) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography color="error" variant="h6">{error}</Typography>
          <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
            Back to Accounts
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Breadcrumb
        items={[
          {
            label: 'Accounts',
            path: '/accounts',
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: isEditMode ? `Edit ${account?.name}` : 'Create Account',
          }
        ]}
      />

      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          {pageTitle}
        </Typography>

        {isDeactivated && (
          <Alert severity="error" sx={{ mb: 3, fontWeight: 600 }}>
            {originalActive
              ? 'This Account will be deactivated. No changes can be made until it is reactivated.'
              : 'This Account is deactivated.'}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Basic Account Information
              </Typography>
              <Divider sx={{ mb: 3, width: '100%' }} />

              <Stack spacing={3}>
                {isEditMode && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${formActive ? theme.palette.success.light : theme.palette.error.light}`,
                      backgroundColor: formActive
                        ? alpha(theme.palette.success.main, 0.04)
                        : alpha(theme.palette.error.main, 0.04),
                    }}
                  >
                    {formActive ? (
                      <CheckCircleOutlineIcon color="success" />
                    ) : (
                      <BlockIcon color="error" />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formActive}
                          onChange={(e) => setFormActive(e.target.checked)}
                          color={formActive ? 'success' : 'error'}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formActive ? 'Account Active' : 'Account Inactive'}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {formActive
                        ? 'Uncheck to deactivate this account'
                        : 'Check to reactivate this account'}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <TextField
                    fullWidth
                    required
                    id="name"
                    label="Account Name"
                    value={formState.name}
                    onChange={(e: any) => handleInputChange('name', e.target.value)}
                    variant="outlined"
                    disabled={!!isDeactivated}
                  />
                </Box>

                <Box>
                  <FormControl fullWidth disabled={!!isDeactivated}>
                    <InputLabel id="line-of-business-label">Line of Business</InputLabel>
                    <Select
                      labelId="line-of-business-label"
                      id="lineOfBusiness"
                      value={formState.lineOfBusiness}
                      label="Line of Business"
                      onChange={(e: any) => handleInputChange('lineOfBusiness', e.target.value)}
                    >
                      {lineOfBusinessOptions.map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    id="supersetRole"
                    label="Superset Role"
                    value={formState.supersetRole}
                    onChange={(e) => {
                      const value = e.target.value;
                      const limitedValue = value.slice(0, 80);
                      handleInputChange('supersetRole', limitedValue);
                    }}
                    variant="outlined"
                    helperText="Exactly match Superset Role name."
                    inputProps={{ maxLength: 80 }}
                    disabled={!!isDeactivated}
                  />
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.ssoEnabled}
                        onChange={(e: any) => handleSsoToggle(e.target.checked)}
                        disabled={!!isDeactivated}
                      />
                    }
                    label="SSO Enabled"
                  />
                  <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                    Enable Single Sign-On authentication for this account
                  </FormHelperText>
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.persistent}
                        onChange={(e: any) => handleInputChange('persistent', e.target.checked)}
                        disabled={!!isDeactivated}
                      />
                    }
                    label="Persistent"
                  />
                  <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                    Exempt this account from automatic deactivation due to inactivity
                  </FormHelperText>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                Access Control
              </Typography>
              <Divider sx={{ mb: 3, width: '100%' }} />

              <Stack spacing={2}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.listenPortalEnabled}
                        onChange={(e: any) => handleInputChange('listenPortalEnabled', e.target.checked)}
                        disabled={!!isDeactivated}
                      />
                    }
                    label="Access to Listen Web"
                  />
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.strategyPortalEnabled}
                        onChange={(e: any) => handleInputChange('strategyPortalEnabled', e.target.checked)}
                        disabled={!!isDeactivated}
                      />
                    }
                    label="Access to Strategy Edge"
                  />
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.listenReportingOnly}
                        onChange={(e: any) => handleInputChange('listenReportingOnly', e.target.checked)}
                        disabled={!!isDeactivated}
                      />
                    }
                    label="Listen Reporting Only"
                  />
                </Box>

                {/* SFDC Account Id field - visible when Listen Reporting Only is enabled */}
                {formState.listenReportingOnly && (
                  <Box sx={{ pl: 4, pt: 1 }}>
                    <TextField
                      fullWidth
                      id="sfdcAccountId"
                      label="SFDC Account Id"
                      value={formState.sfdcAccountId}
                      onChange={(e: any) => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                        handleInputChange('sfdcAccountId', value);
                      }}
                      placeholder="SFDC Account Id"
                      variant="outlined"
                      helperText="Alphanumeric characters only"
                      inputProps={{ pattern: '[a-zA-Z0-9]*' }}
                      disabled={!!isDeactivated}
                    />
                  </Box>
                )}

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.selectiveAccess}
                        onChange={(e: any) => handleInputChange('selectiveAccess', e.target.checked)}
                        disabled={!!isDeactivated || (!isEditMode && companyHierarchyUnavailable)}
                      />
                    }
                    label="Selective Access"
                  />
                  {!isEditMode && companyHierarchyUnavailable && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                      Industry data is unavailable, so selective access can't be configured right now.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>

            {(isEditMode || dashboards.length > 0) && (
              <Box>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                  Dashboard Access
                </Typography>
                <Divider sx={{ mb: 3, width: '100%' }} />

                <Stack spacing={4}>
                  <Box>
                    <MultiSelect
                      id="embeddedDashboards"
                      label="Embedded Dashboard Access"
                      disabled={!!isDeactivated}
                      options={[
                        ...(dashboards.filter(dashboard =>
                          !formState.hiddenDashboards.some(hidden => hidden.id === dashboard.id) &&
                          !formState.embeddedDashboards.some(embedded => embedded.id === dashboard.id)
                        ).length > 0 ? [{ id: -1, name: 'Select All', type: 'select-all' }] : []),
                        ...dashboards.filter(dashboard =>
                          !formState.hiddenDashboards.some(hidden => hidden.id === dashboard.id) &&
                          !formState.embeddedDashboards.some(embedded => embedded.id === dashboard.id)
                        ).map(dashboard => ({
                          ...dashboard,
                          name: dashboard?.superscript?.trim() ? `${dashboard.name} - ${dashboard.superscript}` : dashboard.name
                        }))
                      ]}
                      value={formState.embeddedDashboards.map(dashboard => ({
                        ...dashboard,
                        name: dashboard?.superscript?.trim() ? `${dashboard.name} - ${dashboard.superscript}` : dashboard.name
                      }))}
                      onChange={(event, value) => {
                        if (value.find(option => option.id === -1)) {
                          handleSelectAllDashboards('embedded');
                          return;
                        }
                        const filteredValue = value.filter(option => option.id !== -1) as Dashboard[];
                        handleEmbeddedDashboardChange(event, filteredValue);
                      }}
                      placeholder="Select dashboards"
                      groupBy={(option) => option.id === -1 ? '' : ('category' in option && option.category ? option.category.name : 'Uncategorized')}
                      renderGroup={(params) => {
                        const displayCategory = params.group;
                        if (!displayCategory) {
                          return <div key={params.key}>{params.children}</div>;
                        }
                        return (
                          <div key={params.key}>
                            <div style={{
                              padding: '10px',
                              backgroundColor: theme.palette.mode === 'dark'
                                ? theme.palette.grey[900] : theme.palette.grey[50],
                              color: theme.palette.text.secondary,
                              fontSize: '0.875rem',
                              borderRadius: '4px',
                              margin: '4px 0'
                            }}>
                              {displayCategory}
                            </div>
                            {params.children}
                          </div>
                        );
                      }}
                      helperText="Select dashboards that should be accessible"
                    />
                  </Box>

                  <Box>
                    <MultiSelect
                      id="hiddenDashboards"
                      label="Hidden Dashboard Access"
                      disabled={!!isDeactivated}
                      options={[
                        ...(dashboards.filter(dashboard =>
                          !formState.hiddenDashboards.some(hidden => hidden.id === dashboard.id) &&
                          !formState.embeddedDashboards.some(embedded => embedded.id === dashboard.id)
                        ).length > 0 ? [{ id: -1, name: 'Select All', type: 'select-all' }] : []),
                        ...dashboards.filter(dashboard =>
                          !formState.hiddenDashboards.some(hidden => hidden.id === dashboard.id) &&
                          !formState.embeddedDashboards.some(embedded => embedded.id === dashboard.id)
                        ).map(dashboard => ({
                          ...dashboard,
                          name: dashboard.superscript ? `${dashboard.name} - ${dashboard.superscript}` : dashboard.name
                        }))
                      ]}
                      value={formState.hiddenDashboards.map(dashboard => ({
                        ...dashboard,
                        name: dashboard.superscript ? `${dashboard.name} - ${dashboard.superscript}` : dashboard.name
                      }))}
                      onChange={(event, value) => {
                        if (value.find(option => option.id === -1)) {
                          handleSelectAllDashboards('hidden');
                          return;
                        }
                        const filteredValue = value.filter(option => option.id !== -1) as Dashboard[];
                        handleHiddenDashboardChange(event, filteredValue);
                      }}
                      placeholder="Select dashboards to hide"
                      groupBy={(option) => option.id === -1 ? '' : ('category' in option && option.category ? option.category.name : 'Uncategorized')}
                      renderGroup={(params) => {
                        const displayCategory = params.group;
                        if (!displayCategory) {
                          return <div key={params.key}>{params.children}</div>;
                        }
                        return (
                          <div key={params.key}>
                            <div style={{
                              padding: '10px',
                              backgroundColor: theme.palette.mode === 'dark'
                                ? theme.palette.grey[900] : theme.palette.grey[50],
                              color: theme.palette.text.secondary,
                              fontSize: '0.875rem',
                              borderRadius: '4px',
                              margin: '4px 0'
                            }}>
                              {displayCategory}
                            </div>
                            {params.children}
                          </div>
                        );
                      }}
                      helperText="Select dashboards that should be hidden"
                    />
                  </Box>
                </Stack>
              </Box>
            )}

            {/* PLC section overrides live in plc-svc; staged edits are applied on Save Changes */}
            {isEditMode && plcSectionsService.isConfigured() && accountSupportsPlc(formState.lineOfBusiness) && (
              <PlcSectionAccessPanel ref={plcPanelRef} accountId={Number(id)} disabled={!!isDeactivated} />
            )}

            {/* Only display industry & company section when selective access is checked */}
            {formState.selectiveAccess && companyHierarchy && (
              <Box 
                sx={{ 
                  mt: 4, 
                  p: 2, 
                  border: '2px solid #f0f0f0',
                  borderRadius: 2,
                  transition: 'all 0.3s ease'
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Industry & Company Access
                </Typography>
                <Divider sx={{ mb: 3, width: '100%' }} />

                <Stack spacing={4}>
                  <Box>
                    <MultiSelect
                      id="industryAccess"
                      label="Industry Permission Access"
                      options={[
                        ...(companyHierarchy.companyHierarchy.sectorList.flatMap(sector =>
                          companyHierarchy.companyHierarchy.sectors[sector].industryList.map(industry => {
                            const industryData = companyHierarchy.companyHierarchy.sectors[sector].industries[industry];
                            return {
                              id: industryData.industryId,
                              name: industryData.industryName,
                              sector: companyHierarchy.companyHierarchy.sectors[sector].sectorName
                            };
                          })
                        ).filter(industry =>
                          !formState.selectedIndustries.some(selected => selected.id === industry.id)
                        ).length > 0 ? [{ name: 'Select All', id: -1, sector: '' }] : []),
                        ...companyHierarchy.companyHierarchy.sectorList.flatMap(sector =>
                          companyHierarchy.companyHierarchy.sectors[sector].industryList.map(industry => {
                            const industryData = companyHierarchy.companyHierarchy.sectors[sector].industries[industry];
                            return {
                              id: industryData.industryId,
                              name: industryData.industryName,
                              sector: companyHierarchy.companyHierarchy.sectors[sector].sectorName
                            };
                          })
                        ).filter(industry =>
                          !formState.selectedIndustries.some(selected => selected.id === industry.id)
                        )
                      ]}
                      value={formState.selectedIndustries}
                      onChange={(event, value) => {
                        if (value.some(v => v.id === -1)) {
                          handleSelectAllIndustries();
                          return;
                        }
                        handleIndustryChange(event, value);
                      }}
                      placeholder="Select industries"
                      groupBy={(option) => {
                        if (option.id === -1) return '';
                        return option.sector || 'Other';
                      }}
                      renderGroup={(params) => {
                        const displaySector = params.group;
                        if (!displaySector) {
                          return <div key={params.key}>{params.children}</div>;
                        }
                        return (
                          <div key={params.key}>
                            <div style={{
                              padding: '10px',
                              backgroundColor: theme.palette.mode === 'dark'
                                ? theme.palette.grey[900] : theme.palette.grey[50],
                              color: theme.palette.text.secondary,
                              fontSize: '0.875rem',
                              borderRadius: '4px',
                              margin: '4px 0'
                            }}>
                              {displaySector}
                            </div>
                            {params.children}
                          </div>
                        );
                      }}
                      helperText="Select industries to grant access to"
                    />
                  </Box>

                  {formState.selectedIndustries.length > 0 && (
                    <Box>
                      {formState.selectedIndustries.map((industry, industryIndex) => {
                        // Direct O(1) lookup — the selection carries its sector, and the hierarchy is
                        // keyed by sector then industry name.
                        const industryData = companyHierarchy.companyHierarchy.sectors[industry.sector]
                          ?.industries[industry.name];
                        const companiesList = industryData
                          ? Object.values(industryData.companies).map(company => ({
                              id: company.companyId,
                              name: company.companyName,
                              sector: industry.sector,
                              industry: industry.name
                            }))
                          : [];

                        return (
                          <Box key={`industry-${industryIndex}-${industry.id}`}>
                            <Typography variant="subtitle1" gutterBottom>
                              {industry.name} Companies:
                            </Typography>
                            <MultiSelect
                              id={`companies-${industryIndex}-${industry.id}`}
                              label={`Companies in ${industry.name}`}
                              options={[
                                ...(companiesList.filter(companyObj =>
                                  !(formState.selectedCompanies[industry.id] || []).some(selected => selected.id === companyObj.id)
                                ).length > 0 ? [{ name: 'Select All', id: -1 }] : []),
                                ...companiesList.filter(companyObj =>
                                  !(formState.selectedCompanies[industry.id] || []).some(selected => selected.id === companyObj.id)
                                )
                              ]}
                              value={(formState.selectedCompanies[industry.id] || [])}
                              onChange={(e, value) => {
                                if (value.some(v => v.id === -1)) {
                                  handleSelectAllCompanies(industry);
                                  return;
                                }
                                handleCompanyChange(industry.id, value);
                              }}
                              placeholder="Select companies"
                              helperText={`Select companies in ${industry.name}`}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
            
            {/* Show a message when selective access is disabled */}
            {!formState.selectiveAccess && (
              <Box sx={{ mt: 4, p: 2, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                <Typography color="text.secondary" align="center">
                  Enable Selective Access to configure industry and company restrictions
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                  startIcon={<CancelIcon />}
                  disabled={saving}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 2
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={saving || !formState.name || (!!isDeactivated && !activeChanged)}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 2
                  }}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Account'}
                </Button>
              </Box>
            </Box>
          </Stack>
        </form>
      </Paper>

      {/* SSO Change Confirmation Modal */}
      <ConfirmationDialog
        open={showSsoModal}
        title={pendingSsoValue ? 'Enable SSO' : 'Disable SSO'}
        content={`This will change how users sign in to the account and can break the application for these users.\n\nAre you sure you want to do this?`}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        onConfirm={handleSsoModalConfirm}
        onCancel={handleSsoModalCancel}
        severity="warning"
      />
    </Container>
  );
};

export default EditAccountPage;